import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import {
  getCropWiseNutrientList,
  exportNutrientsExcel,
  getNutrientCrops,
  getNutrientVarietiesByCrop,
  getVarietyNutrientRequirements,
  saveVarietyNutrientRequirements,
  importNutrientsExcel,
  getCountries
} from "../lib/api";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.soksan-dss.com";

const NUTRIENT_COLUMNS = [
  { key: "nitrogen_n_kg_ha", label: "Nitrogen (N)", short: "N", shortLabel: "N" },
  { key: "phosphorus_p2o5_kg_ha", label: "Phosphorus (P₂O₅)", short: "P₂O₅", shortLabel: "P₂O₅" },
  { key: "potassium_k2o_kg_ha", label: "Potassium (K₂O)", short: "K₂O", shortLabel: "K₂O" },
  { key: "sulfur_s_kg_ha", label: "Sulfur (S)", short: "S", shortLabel: "S" },
  { key: "calcium_ca_kg_ha", label: "Calcium (Ca)", short: "Ca", shortLabel: "Ca" },
  { key: "magnesium_mg_kg_ha", label: "Magnesium (Mg)", short: "Mg", shortLabel: "Mg" },
  { key: "zinc_zn_kg_ha", label: "Zinc (Zn)", short: "Zn", shortLabel: "Zn" },
  { key: "boron_b_kg_ha", label: "Boron (B)", short: "B", shortLabel: "B" },
  { key: "iron_fe_kg_ha", label: "Iron (Fe)", short: "Fe", shortLabel: "Fe" },
  { key: "manganese_mn_kg_ha", label: "Manganese (Mn)", short: "Mn", shortLabel: "Mn" },
  { key: "copper_cu_kg_ha", label: "Copper (Cu)", short: "Cu", shortLabel: "Cu" },
];

const getToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");

const VarietyWiseNutrientList = () => {
  const [viewMode, setViewMode] = useState("list");
  const [cropsData, setCropsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cropFilter, setCropFilter] = useState("all");
  const [cropDropdownOpen, setCropDropdownOpen] = useState(false);
  const [cropSearchTerm, setCropSearchTerm] = useState("");
  const [searchText, setSearchText] = useState("");
  const [dataStatusFilter, setDataStatusFilter] = useState("all");
  const cropRef = useRef(null);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedVarietyIds, setSelectedVarietyIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const [editTab, setEditTab] = useState("screen");
  const [allCrops, setAllCrops] = useState([]);
  const [loadingCrops, setLoadingCrops] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [editCropDropdownOpen, setEditCropDropdownOpen] = useState(false);
  const [editCropSearchTerm, setEditCropSearchTerm] = useState("");
  const [varieties, setVarieties] = useState([]);
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [varietyDropdownOpen, setVarietyDropdownOpen] = useState(false);
  const [varietySearchTerm, setVarietySearchTerm] = useState("");
  const [matrixPhases, setMatrixPhases] = useState([]);
  const [matrixValues, setMatrixValues] = useState({});
  const [initialMatrixValues, setInitialMatrixValues] = useState({});
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [hasExistingSavedData, setHasExistingSavedData] = useState(false);
  const editCropRef = useRef(null);
  const varietyRef = useRef(null);

  const [exportSelectedCrops, setExportSelectedCrops] = useState([]);
  const [exportCropSearch, setExportCropSearch] = useState("");
  const [exportVarietiesMap, setExportVarietiesMap] = useState({});
  const [exportSelectedVarieties, setExportSelectedVarieties] = useState([]);
  const [exportVarietySearch, setExportVarietySearch] = useState("");
  const [loadingExportVarieties, setLoadingExportVarieties] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccessInfo, setImportSuccessInfo] = useState(null);
  const fileInputRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [selectedCountryIds, setSelectedCountryIds] = useState([]); // multi-select
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const countryRef = useRef(null);

  const parseAllowedCountries = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) {
      return val
        .map((item) =>
          typeof item === 'object' && item !== null
            ? String(item.ID || item.id || item.CountryID || '')
            : String(item)
        )
        .filter(Boolean);
    }
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) =>
              typeof item === 'object' && item !== null
                ? String(item.ID || item.id || item.CountryID || '')
                : String(item)
            )
            .filter(Boolean);
        }
      } catch {
        return [val.trim()].filter(Boolean);
      }
    }
    return [];
  };

  const getCountryId = (c) => String(c?.ID || c?.id || c?.CountryID || '');

  const fetchCountriesList = useCallback(async () => {
    try {
      setLoadingCountries(true);
      const token = getToken();
      const res = await getCountries(token);
      // API returns { success, countries: [...] }
      const list = res?.countries || res?.data || (Array.isArray(res) ? res : []);
      const sorted = [...list].sort((a, b) =>
        (a.CountryName || "").localeCompare(b.CountryName || "")
      );
      setCountries(sorted);
    } catch (err) {
      console.error("Error fetching countries:", err);
      toast.error(err?.response?.data?.message || "Failed to load countries.");
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  useEffect(() => { fetchCountriesList(); }, [fetchCountriesList]);
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const res = await getCropWiseNutrientList(token);
      const list = res?.data || (Array.isArray(res) ? res : []);
      const sorted = [...list].sort((a, b) => (a.CropName || "").localeCompare(b.CropName || ""));
      setCropsData(sorted);
    } catch (err) {
      console.error("Error fetching crop-wise nutrient list:", err);
      toast.error(err?.response?.data?.message || "Failed to load nutrient list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchCropsList = useCallback(async () => {
    try {
      setLoadingCrops(true);
      const token = getToken();
      const res = await getNutrientCrops(token);
      if (res && res.data) {
        const sorted = [...res.data].sort((a, b) => (a.CropName || "").localeCompare(b.CropName || ""));
        setAllCrops(sorted);
      }
    } catch (err) {
      console.error("Error fetching crops:", err);
      toast.error(err?.response?.data?.message || "Failed to load crops list.");
    } finally {
      setLoadingCrops(false);
    }
  }, []);

  useEffect(() => { fetchCropsList(); }, [fetchCropsList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cropRef.current && !cropRef.current.contains(event.target)) setCropDropdownOpen(false);
      if (editCropRef.current && !editCropRef.current.contains(event.target)) setEditCropDropdownOpen(false);
      if (varietyRef.current && !varietyRef.current.contains(event.target)) setVarietyDropdownOpen(false);
      if (countryRef.current && !countryRef.current.contains(event.target)) setCountryDropdownOpen(false); // 👈 ADD
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
const filteredCountriesForDropdown = useMemo(() => {
    if (!countrySearchTerm.trim()) return countries;
    const q = countrySearchTerm.toLowerCase();
    return countries.filter(
      (c) =>
        (c.CountryName && c.CountryName.toLowerCase().includes(q)) ||
        (c.CountryCode && c.CountryCode.toLowerCase().includes(q))
    );
  }, [countries, countrySearchTerm]);

  const handleToggleCountry = (id) => {
    const strId = String(id);
    setSelectedCountryIds((prev) =>
      prev.includes(strId) ? prev.filter((cId) => cId !== strId) : [...prev, strId]
    );
  };

  const handleToggleSelectAllCountries = () => {
    if (selectedCountryIds.length === countries.length) {
      setSelectedCountryIds([]);
    } else {
      setSelectedCountryIds(countries.map(getCountryId).filter(Boolean));
    }
  };

  const handleClearCountries = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedCountryIds([]);
  };

  const filteredCropsData = useMemo(() => {
    if (selectedCountryIds.length === 0) return cropsData;

    const selectedSet = new Set(selectedCountryIds.map(String));

    return cropsData
      .filter((crop) => {
        const cropAllowed = parseAllowedCountries(crop.AllowedCountries);
        return cropAllowed.some((cId) => selectedSet.has(cId));
      })
      .map((crop) => {
        const matchingVarieties = (crop.varieties || []).filter((variety) => {
          const varAllowed = parseAllowedCountries(variety.AllowedCountries);
          if (varAllowed.length > 0) {
            return varAllowed.some((vId) => selectedSet.has(vId));
          }
          const cropAllowed = parseAllowedCountries(crop.AllowedCountries);
          return cropAllowed.some((cId) => selectedSet.has(cId));
        });

        return {
          ...crop,
          varieties: matchingVarieties,
          varieties_count: matchingVarieties.length,
        };
      })
      .filter((crop) => (crop.varieties || []).length > 0);
  }, [cropsData, selectedCountryIds]);

  const allRows = useMemo(() => {
    const rows = [];
    for (const crop of filteredCropsData) {
      const varieties = crop.varieties || [];
      for (const variety of varieties) {
        const phases = variety.phases || variety.parsedPhases || crop.phases || [];
        const requirements = variety.requirements || variety.nutrient_requirements || variety.existing_requirements || [];
        const reqMap = {};
        for (const r of requirements) reqMap[String(r.phase_id)] = r;
        const totals = {};
        for (const nut of NUTRIENT_COLUMNS) {
          let sum = 0; let has = false;
          for (const req of requirements) {
            const v = req[nut.key];
            if (v !== null && v !== undefined && !isNaN(Number(v))) { sum += Number(v); has = true; }
          }
          totals[nut.key] = has ? Math.round(sum * 100) / 100 : null;
        }
        rows.push({
          key: `${crop.ID}_${variety.ID}`,
          cropId: crop.ID, cropName: crop.CropName, cropShortName: crop.CropShortName,
          cropCategory: crop.Category, cropImage: crop.FeatureImageFileName,
          varietyId: variety.ID, varietyName: variety.VariantName,
          phases, requirements, reqMap, totals,
          hasData: requirements.length > 0, phaseCount: phases.length,
        });
      }
    }
    return rows;
  }, [filteredCropsData]);

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (cropFilter !== "all") rows = rows.filter((r) => r.cropId === cropFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      rows = rows.filter((r) => r.cropName?.toLowerCase().includes(q) || r.varietyName?.toLowerCase().includes(q));
    }
    if (dataStatusFilter !== "all") rows = rows.filter((r) => dataStatusFilter === "has" ? r.hasData : !r.hasData);
    return rows;
  }, [allRows, cropFilter, searchText, dataStatusFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const paged = filteredRows.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [cropFilter, searchText, dataStatusFilter, perPage, selectedCountryIds]);

  const handleToggleVarietySelect = (varietyId) => {
    setSelectedVarietyIds((prev) => prev.includes(varietyId) ? prev.filter((id) => id !== varietyId) : [...prev, varietyId]);
  };

  const handleSelectAllOnPage = () => {
    const pageIds = paged.map((r) => r.varietyId);
    const allSelected = pageIds.every((id) => selectedVarietyIds.includes(id));
    if (allSelected) setSelectedVarietyIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    else setSelectedVarietyIds((prev) => [...new Set([...prev, ...pageIds])]);
  };

  const handleExportSelected = async () => {
    if (selectedVarietyIds.length === 0) { toast.warning("Please select at least one variety to export."); return; }
    try {
      setIsExporting(true);
      const cropIdsSet = new Set(allRows.filter((r) => selectedVarietyIds.includes(r.varietyId)).map((r) => r.cropId));
      const payload = { crop_ids: Array.from(cropIdsSet), variety_ids: selectedVarietyIds };
      const res = await exportNutrientsExcel(payload, getToken());
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `Variety_Nutrient_List_${Date.now()}.xlsx`);
      toast.success("Excel file generated and download started!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to export Excel file.");
    } finally { setIsExporting(false); }
  };

  const filteredCropsForDropdown = useMemo(() => {
    let list = filteredCropsData;
    if (cropSearchTerm.trim()) {
      const q = cropSearchTerm.toLowerCase();
      list = list.filter((c) => c.CropName?.toLowerCase().includes(q) || c.CropShortName?.toLowerCase().includes(q));
    }
    return list;
  }, [filteredCropsData, cropSearchTerm]);

  useEffect(() => {
    if (cropFilter !== 'all' && !filteredCropsData.some((c) => String(c.ID) === String(cropFilter))) {
      setCropFilter('all');
    }
  }, [filteredCropsData, cropFilter]);

  const renderCropImage = (imgName) => {
    if (!imgName) return (
      <div className="w-32-px h-32-px radius-4 bg-light d-flex align-items-center justify-content-center" style={{ fontSize: 14 }}>
        <Icon icon="mdi:barley" className="text-secondary-light" />
      </div>
    );
    return <img src={`${API_BASE}/uploads/crop/${imgName}`} alt="crop" width="32" height="32" className="rounded" style={{ objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />;
  };

  const resetEditState = () => {
    setSelectedCrop(null); setSelectedVariety(null); setVarieties([]);
    setMatrixPhases([]); setMatrixValues({}); setInitialMatrixValues({});
    setHasExistingSavedData(false); setEditTab("screen");
    setEditCropDropdownOpen(false); setEditCropSearchTerm("");
    setVarietyDropdownOpen(false); setVarietySearchTerm("");
  };

  const loadVarietyMatrix = async (variety) => {
    if (!variety) { setMatrixPhases([]); setMatrixValues({}); setInitialMatrixValues({}); setHasExistingSavedData(false); return; }
    try {
      setMatrixLoading(true);
      const res = await getVarietyNutrientRequirements(variety.ID, getToken());
      if (res && res.data) {
        const phasesData = res.data.phases || [];
        const existingData = res.data.existing_requirements || [];
        setMatrixPhases(phasesData);
        setHasExistingSavedData(existingData.length > 0);
        const existingMap = new Map();
        for (const req of existingData) existingMap.set(String(req.phase_id), req);
        const valuesObj = {};
        for (const nut of NUTRIENT_COLUMNS) {
          valuesObj[nut.key] = {};
          for (const ph of phasesData) {
            const pId = String(ph.PhaseID || ph.id);
            const req = existingMap.get(pId);
            const val = req && req[nut.key] !== null && req[nut.key] !== undefined ? String(req[nut.key]) : "";
            valuesObj[nut.key][pId] = val;
          }
        }
        setMatrixValues(valuesObj);
        setInitialMatrixValues(JSON.parse(JSON.stringify(valuesObj)));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch nutrient matrix for variety.");
    } finally { setMatrixLoading(false); }
  };

  const handleOpenEditForRow = async (row) => {
    resetEditState();
    setViewMode("edit");
    const cropObj = allCrops.find((c) => String(c.ID) === String(row.cropId)) || cropsData.find((c) => String(c.ID) === String(row.cropId)) || { ID: row.cropId, CropName: row.cropName };
    setSelectedCrop(cropObj);
    setLoadingVarieties(true);
    try {
      const res = await getNutrientVarietiesByCrop(cropObj.ID, getToken());
      const sorted = [...(res?.data || [])].sort((a, b) => (a.VariantName || "").localeCompare(b.VariantName || ""));
      setVarieties(sorted);
      const variety = sorted.find((v) => String(v.ID) === String(row.varietyId)) || { ID: row.varietyId, VariantName: row.varietyName };
      setSelectedVariety(variety);
      await loadVarietyMatrix(variety);
    } catch (err) {
      toast.error("Failed to open nutrient editor.");
    } finally { setLoadingVarieties(false); }
  };

  const handleSelectCropInEdit = async (crop) => {
    setSelectedCrop(crop); setEditCropDropdownOpen(false); setEditCropSearchTerm("");
    setSelectedVariety(null); setVarieties([]); setMatrixPhases([]);
    setMatrixValues({}); setInitialMatrixValues({}); setHasExistingSavedData(false);
    if (!crop) return;
    try {
      setLoadingVarieties(true);
      const res = await getNutrientVarietiesByCrop(crop.ID, getToken());
      if (res && res.data) {
        const sorted = [...res.data].sort((a, b) => (a.VariantName || "").localeCompare(b.VariantName || ""));
        setVarieties(sorted);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load varieties for selected crop.");
    } finally { setLoadingVarieties(false); }
  };

  const handleSelectVarietyInEdit = async (variety) => {
    setSelectedVariety(variety); setVarietyDropdownOpen(false); setVarietySearchTerm("");
    await loadVarietyMatrix(variety);
  };

  const handleBackToList = () => { resetEditState(); setViewMode("list"); fetchAll(); };

  const filteredEditCrops = useMemo(() => {
    if (!editCropSearchTerm.trim()) return allCrops;
    const q = editCropSearchTerm.toLowerCase();
    return allCrops.filter((c) => (c.CropName && c.CropName.toLowerCase().includes(q)) || (c.CropShortName && c.CropShortName.toLowerCase().includes(q)));
  }, [allCrops, editCropSearchTerm]);

  const filteredVarieties = useMemo(() => {
    if (!varietySearchTerm.trim()) return varieties;
    const q = varietySearchTerm.toLowerCase();
    return varieties.filter((v) => v.VariantName && v.VariantName.toLowerCase().includes(q));
  }, [varieties, varietySearchTerm]);

  const handleCellChange = (nutrientKey, phaseId, rawVal) => {
    if (rawVal !== "" && !/^\d*\.?\d*$/.test(rawVal)) return;
    setMatrixValues((prev) => ({ ...prev, [nutrientKey]: { ...(prev[nutrientKey] || {}), [phaseId]: rawVal } }));
  };

  const handleFillZeroEmpty = () => {
    setMatrixValues((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      for (const nut of NUTRIENT_COLUMNS) {
        if (!next[nut.key]) next[nut.key] = {};
        for (const ph of matrixPhases) {
          const pId = String(ph.PhaseID || ph.id);
          if (next[nut.key][pId] === "" || next[nut.key][pId] === undefined || next[nut.key][pId] === null) next[nut.key][pId] = "0";
        }
      }
      return next;
    });
    toast.info("Empty cells populated with 0.");
  };

  const handleClearAll = () => {
    setMatrixValues((prev) => {
      const next = {};
      for (const nut of NUTRIENT_COLUMNS) {
        next[nut.key] = {};
        for (const ph of matrixPhases) next[nut.key][String(ph.PhaseID || ph.id)] = "";
      }
      return next;
    });
  };

  const handleResetToSaved = () => {
    setMatrixValues(JSON.parse(JSON.stringify(initialMatrixValues)));
    toast.info("Reset to server saved values.");
  };

  const calculateNutrientTotal = (nutrientKey) => {
    let total = 0; let hasValue = false;
    for (const ph of matrixPhases) {
      const pId = String(ph.PhaseID || ph.id);
      const val = matrixValues[nutrientKey]?.[pId];
      if (val !== "" && val !== null && val !== undefined && !isNaN(Number(val))) { total += Number(val); hasValue = true; }
    }
    return hasValue ? Math.round(total * 100) / 100 : "-";
  };

  const handleSaveMatrix = async () => {
    if (!selectedCrop || !selectedVariety) { toast.warning("Please select both a crop and a variety."); return; }
    if (!matrixPhases || matrixPhases.length === 0) { toast.warning("No phases available for this variety to save."); return; }
    try {
      setSavingMatrix(true);
      const phase_nutrients = matrixPhases.map((ph) => {
        const pId = String(ph.PhaseID || ph.id);
        const item = { phase_id: pId };
        for (const nut of NUTRIENT_COLUMNS) {
          const raw = matrixValues[nut.key]?.[pId];
          if (raw !== "" && raw !== null && raw !== undefined && !isNaN(Number(raw))) item[nut.key] = Number(raw);
          else item[nut.key] = null;
        }
        return item;
      });
      const payload = { crop_id: selectedCrop.ID, variety_id: selectedVariety.ID, phase_nutrients };
      const res = await saveVarietyNutrientRequirements(payload, getToken());
      toast.success(res?.message || "Phase-wise nutritive values saved successfully!");
      setHasExistingSavedData(true);
      setInitialMatrixValues(JSON.parse(JSON.stringify(matrixValues)));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save nutrient requirements.");
    } finally { setSavingMatrix(false); }
  };

  const handleToggleExportCrop = async (cropId) => {
    if (exportSelectedCrops.includes(cropId)) {
      const nextSelected = exportSelectedCrops.filter((id) => id !== cropId);
      setExportSelectedCrops(nextSelected);
      const remaining = [];
      for (const [cId, vList] of Object.entries(exportVarietiesMap)) if (cId !== cropId) remaining.push(...vList.map((v) => v.ID));
      setExportSelectedVarieties((prev) => prev.filter((vId) => remaining.includes(vId)));
    } else {
      setExportSelectedCrops([...exportSelectedCrops, cropId]);
      if (!exportVarietiesMap[cropId]) {
        try {
          setLoadingExportVarieties(true);
          const res = await getNutrientVarietiesByCrop(cropId, getToken());
          if (res && res.data) {
            setExportVarietiesMap((prev) => ({ ...prev, [cropId]: res.data }));
            setExportSelectedVarieties((prev) => [...prev, ...res.data.map((v) => v.ID)]);
          }
        } catch (err) { console.error(err); }
        finally { setLoadingExportVarieties(false); }
      } else {
        const toAdd = (exportVarietiesMap[cropId] || []).map((v) => v.ID);
        setExportSelectedVarieties((prev) => [...new Set([...prev, ...toAdd])]);
      }
    }
  };

  const handleSelectAllExportCrops = async () => {
    if (exportSelectedCrops.length === allCrops.length) { setExportSelectedCrops([]); setExportSelectedVarieties([]); return; }
    const allCropIds = allCrops.map((c) => c.ID);
    setExportSelectedCrops(allCropIds);
    try {
      setLoadingExportVarieties(true);
      const newMap = { ...exportVarietiesMap };
      const allVarIds = [];
      await Promise.all(allCropIds.map(async (cId) => {
        if (!newMap[cId]) {
          try { const res = await getNutrientVarietiesByCrop(cId, getToken()); if (res && res.data) newMap[cId] = res.data; } catch (e) { }
        }
        if (newMap[cId]) allVarIds.push(...newMap[cId].map((v) => v.ID));
      }));
      setExportVarietiesMap(newMap);
      setExportSelectedVarieties(allVarIds);
    } catch (e) { console.error(e); }
    finally { setLoadingExportVarieties(false); }
  };

  const handleToggleExportVariety = (vId) => {
    if (exportSelectedVarieties.includes(vId)) setExportSelectedVarieties(exportSelectedVarieties.filter((id) => id !== vId));
    else setExportSelectedVarieties([...exportSelectedVarieties, vId]);
  };

  const allAvailableExportVarieties = useMemo(() => {
    const list = [];
    for (const cId of exportSelectedCrops) if (exportVarietiesMap[cId]) list.push(...exportVarietiesMap[cId]);
    return list;
  }, [exportSelectedCrops, exportVarietiesMap]);

  const handleSelectAllExportVarieties = () => {
    if (exportSelectedVarieties.length === allAvailableExportVarieties.length) setExportSelectedVarieties([]);
    else setExportSelectedVarieties(allAvailableExportVarieties.map((v) => v.ID));
  };

  const handleExportExcel = async () => {
    if (exportSelectedCrops.length === 0) { toast.warning("Please select at least one crop to export."); return; }
    if (exportSelectedVarieties.length === 0) { toast.warning("Please select at least one variety to export."); return; }
    try {
      setIsExportingExcel(true);
      const payload = { crop_ids: exportSelectedCrops, variety_ids: exportSelectedVarieties };
      const res = await exportNutrientsExcel(payload, getToken());
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `Crop_Variety_Phase_Nutrient_Requirements_${Date.now()}.xlsx`);
      toast.success("Excel file generated and download started!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to export Excel file.");
    } finally { setIsExportingExcel(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) { setImportFile(file); setImportErrors([]); setImportSuccessInfo(null); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportFile(e.dataTransfer.files[0]); setImportErrors([]); setImportSuccessInfo(null);
    }
  };

  const handleRemoveImportFile = () => {
    setImportFile(null); setImportErrors([]); setImportSuccessInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImportExcel = async () => {
    if (!importFile) { toast.warning("Please choose an Excel file to import."); return; }
    try {
      setIsImporting(true); setImportErrors([]); setImportSuccessInfo(null);
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("excelFile", importFile);
      const res = await importNutrientsExcel(formData, getToken());
      toast.success(res?.message || "Nutrient requirements successfully imported!");
      setImportSuccessInfo(res?.data || { message: res?.message });
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (selectedVariety) loadVarietyMatrix(selectedVariety);
    } catch (err) {
      const errRes = err?.response?.data;
      if (errRes?.errors && Array.isArray(errRes.errors)) {
        setImportErrors(errRes.errors);
        toast.error(`Import failed with ${errRes.errors.length} validation errors.`);
      } else {
        toast.error(errRes?.message || err.message || "Failed to import Excel file.");
      }
    } finally { setIsImporting(false); }
  };

  // ====================== RENDER LIST VIEW ======================
  const renderListView = () => (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
          <select className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
            {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
            <input type="text" className="bg-base h-40-px w-auto" placeholder="Search crop or variety..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
          <div style={{ minWidth: 220 }} className="position-relative" ref={countryRef}>
            <button
              type="button"
              className="form-select text-start d-flex align-items-center justify-content-between h-40-px"
              onClick={() => setCountryDropdownOpen((v) => !v)}
            >
              <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                <Icon icon="mdi:earth" className="text-secondary-light flex-shrink-0" style={{ fontSize: 18 }} />
                <span className="text-truncate">
                  {selectedCountryIds.length === 0
                    ? "All Countries"
                    : selectedCountryIds.length === 1
                    ? countries.find((c) => getCountryId(c) === selectedCountryIds[0])?.CountryName || "1 Country selected"
                    : `${selectedCountryIds.length} Countries selected`}
                </span>
              </div>
            </button>
            {countryDropdownOpen && (
              <div
                className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden"
                style={{ zIndex: 35, minWidth: 260 }}
              >
                <div className="p-8 border-bottom">
                  <div className="navbar-search position-relative">
                    <input
                      className="bg-base h-36-px ps-30 w-100"
                      placeholder="Search country..."
                      value={countrySearchTerm}
                      onChange={(e) => setCountrySearchTerm(e.target.value)}
                      autoFocus
                    />
                    <Icon icon="ion:search-outline" className="icon" />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between px-16 py-8 border-bottom bg-light">
                  <label className="d-flex align-items-center gap-2 cursor-pointer mb-0 text-xs fw-semibold text-primary-light">
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      checked={countries.length > 0 && selectedCountryIds.length === countries.length}
                      onChange={handleToggleSelectAllCountries}
                    />
                    <span>Select All ({countries.length})</span>
                  </label>
                  {selectedCountryIds.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-xs text-danger text-decoration-none"
                      onClick={() => setSelectedCountryIds([])}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="overflow-auto" style={{ maxHeight: 220 }}>
                  {loadingCountries ? (
                    <div className="p-16 text-center text-secondary-light text-sm">
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Loading countries...
                    </div>
                  ) : filteredCountriesForDropdown.length === 0 ? (
                    <div className="p-16 text-center text-secondary-light text-sm">No countries found</div>
                  ) : (
                    filteredCountriesForDropdown.map((c) => {
                      const cId = getCountryId(c);
                      const isChecked = selectedCountryIds.includes(cId);
                      return (
                        <div
                          key={cId}
                          className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${
                            isChecked ? "bg-primary-focus fw-medium text-primary-600" : "text-secondary-light"
                          }`}
                          onClick={() => handleToggleCountry(cId)}
                        >
                          <label
                            className="d-flex align-items-center gap-2 cursor-pointer mb-0 text-sm flex-grow-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={isChecked}
                              onChange={() => handleToggleCountry(cId)}
                            />
                            <span>{c.CountryName}</span>
                          </label>
                          {c.CountryCode && (
                            <span className="badge bg-light text-secondary border text-xs ms-2">
                              {c.CountryCode}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {selectedCountryIds.length > 0 && (
                  <div className="px-16 py-8 border-top bg-light d-flex justify-content-between align-items-center text-xs text-secondary-light">
                    <span>{selectedCountryIds.length} country(s) selected</span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm py-1 px-8 text-xs"
                      onClick={() => setCountryDropdownOpen(false)}
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ minWidth: 220 }} className="position-relative" ref={cropRef}>
            <button type="button" className="form-select text-start d-flex align-items-center justify-content-between h-40-px" onClick={() => setCropDropdownOpen((v) => !v)}>
              <span className="text-truncate">{cropFilter === "all" ? "All Crops" : filteredCropsData.find((c) => c.ID === cropFilter)?.CropName || "All Crops"}</span>
            </button>
            {cropDropdownOpen && (
              <div className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden" style={{ zIndex: 30 }}>
                <div className="p-8 border-bottom">
                  <div className="navbar-search">
                    <input className="bg-base h-36-px ps-30 w-100" placeholder="Search crop..." value={cropSearchTerm} onChange={(e) => setCropSearchTerm(e.target.value)} autoFocus />
                    <Icon icon="ion:search-outline" className="icon" />
                  </div>
                </div>
                <div className="overflow-auto" style={{ maxHeight: 220 }}>
                  <div className={`px-16 py-8 cursor-pointer hover-bg-light text-sm ${cropFilter === "all" ? "bg-primary-focus text-primary-600 fw-medium" : "text-secondary-light"}`} onClick={() => { setCropFilter("all"); setCropDropdownOpen(false); setCropSearchTerm(""); }}>All Crops</div>
                  {filteredCropsForDropdown.map((c) => (
                    <div key={c.ID} className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${cropFilter === c.ID ? "bg-primary-focus text-primary-600 fw-medium" : "text-secondary-light"}`} onClick={() => { setCropFilter(c.ID); setCropDropdownOpen(false); setCropSearchTerm(""); }}>
                      <span className="text-sm">{c.CropName}</span>
                      <span className="text-xs text-secondary-light">{(c.varieties || []).length}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button type="button" className="btn btn-yellow btn-sm px-12 py-10 radius-8 d-flex align-items-center gap-2" onClick={() => { resetEditState(); setViewMode("edit"); }}>
            <Icon icon="lucide:plus" style={{ fontSize: 16 }} />
            <span>Add / Edit Nutrients</span>
          </button>
          <button type="button" className="btn btn-success text-sm btn-sm px-12 py-10 radius-8 d-flex align-items-center gap-2" onClick={handleExportSelected} disabled={isExporting || selectedVarietyIds.length === 0}>
            {isExporting ? (<><span className="spinner-border spinner-border-sm" role="status" /><span>Exporting...</span></>) : (<><Icon icon="mdi:file-excel" style={{ fontSize: 16 }} /><span>Export Selected</span></>)}
          </button>
        </div>
      </div>
      <div className="card h-100 p-16 radius-12">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0" style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th className="text-center" style={{ width: 40 }}>
                  <input type="checkbox" className="form-check-input" checked={paged.length > 0 && paged.every((r) => selectedVarietyIds.includes(r.varietyId))} onChange={handleSelectAllOnPage} />
                </th>
                <th className="text-start">Crop</th>
                <th className="text-start">Variety</th>
                <th className="text-center">Category</th>
                <th className="text-center">Phases</th>
                <th className="text-center" style={{ width: 100 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-24"><span className="spinner-border spinner-border-sm text-primary me-2" role="status" />Loading nutrient list...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-24 text-secondary-light">No varieties found</td></tr>
              ) : (
                paged.map((row) => {
                  const isSelected = selectedVarietyIds.includes(row.varietyId);
                  return (
                    <tr key={row.key}>
                      <td className="text-center"><input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => handleToggleVarietySelect(row.varietyId)} /></td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {renderCropImage(row.cropImage)}
                          <span className="text-sm fw-medium text-primary-light">{row.cropName}</span>
                        </div>
                      </td>
                      <td><span className="text-sm text-secondary-light">{row.varietyName || "—"}</span></td>
                      <td className="text-center">{row.cropCategory ? <span className="px-8 py-2 radius-4 text-xs bg-light border">{row.cropCategory}</span> : "—"}</td>
                      <td className="text-center fw-semibold text-primary-light">{row.phaseCount}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <button
                            type="button"
                            className="bg-success-100 text-success-600 bg-hover-success-200 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                            title="Edit nutrient values"
                            onClick={() => handleOpenEditForRow(row)}
                          >
                            <Icon icon="lucide:edit" style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span className="text-sm text-secondary-light">
            Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className="page-item">
              <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md" to="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }}>
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {Array.from({ length: pageCount }).map((_, i) => (
              <li key={i} className="page-item">
                <Link className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === i + 1 ? "bg-primary-600 text-white" : "bg-neutral-200"}`} to="#" onClick={(e) => { e.preventDefault(); setPage(i + 1); }}>
                  {i + 1}
                </Link>
              </li>
            ))}
            <li className="page-item">
              <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md" to="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(pageCount, p + 1)); }}>
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  // ====================== RENDER EDIT VIEW ======================
  const renderEditView = () => (
    <div className="card p-0 radius-12">
      <style>{`
      .nutrient-matrix-table > :not(caption) > * > * {
        padding-left: 4px !important;
        padding-right: 4px !important;
      }
      .nutrient-matrix-table thead th {
        padding-top: 6px !important;
        padding-bottom: 6px !important;
        font-size: 12px;
      }
      .nutrient-matrix-table tbody td {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
      }
    `}</style>

      {/* Header */}
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button type="button" className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs" onClick={handleBackToList}>
              <Icon icon="lucide:arrow-left" style={{ fontSize: 14 }} />
              Back to List
            </button>
            <button type="button" className={`btn btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2 text-sm ${editTab === "screen" ? "btn-yellow" : "btn-light"}`} onClick={() => setEditTab("screen")}>
              <Icon icon="mdi:table-edit" style={{ fontSize: 16 }} />
              <span className="text-nowrap">Screen Controls</span>
            </button>
            <button type="button" className={`btn btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2 text-sm ${editTab === "excel" ? "btn-yellow" : "btn-light"}`} onClick={() => setEditTab("excel")}>
              <Icon icon="mdi:file-excel-box" style={{ fontSize: 16 }} />
              <span className="text-nowrap">Excel Import / Export</span>
            </button>
          </div>
          {selectedCrop && selectedVariety && (
            <span className="px-12 py-6 radius-4 text-xs fw-medium bg-primary-focus text-primary-600 border border-primary-main d-inline-flex align-items-center gap-1">
              <Icon icon="lucide:pencil" style={{ fontSize: 12 }} />
              {selectedCrop.CropName} · {selectedVariety.VariantName}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-24">
        {editTab === "screen" && (
          <>
            {/* Selectors */}
            <div className="row g-3 mb-20">
              {/* Crop */}
              <div className="col-md-6 col-lg-5">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2">Crop <span className="text-danger">*</span></label>
                <div className="position-relative" ref={editCropRef}>
                  <button type="button" className="form-select text-start d-flex align-items-center justify-content-between" onClick={() => setEditCropDropdownOpen(!editCropDropdownOpen)} disabled={loadingCrops} style={{ minHeight: 40 }}>
                    <span className={`text-truncate ${selectedCrop ? "text-primary-light fw-medium" : "text-secondary-light"}`}>
                      {selectedCrop ? selectedCrop.CropName : "Select Crop..."}
                    </span>
                  </button>
                  {editCropDropdownOpen && (
                    <div className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden" style={{ zIndex: 30 }}>
                      <div className="p-8 border-bottom">
                        <div className="navbar-search">
                          <input className="bg-base h-36-px ps-30 w-100" placeholder="Search crop..." value={editCropSearchTerm} onChange={(e) => setEditCropSearchTerm(e.target.value)} autoFocus />
                          <Icon icon="ion:search-outline" className="icon" />
                        </div>
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: 240 }}>
                        {loadingCrops ? (
                          <div className="p-16 text-center text-secondary-light text-sm">Loading crops...</div>
                        ) : filteredEditCrops.length === 0 ? (
                          <div className="p-16 text-center text-secondary-light text-sm">No crops found</div>
                        ) : (
                          filteredEditCrops.map((c) => (
                            <div key={c.ID} className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${selectedCrop?.ID === c.ID ? "bg-primary-focus text-primary-600 fw-medium" : "text-secondary-light"}`} onClick={() => handleSelectCropInEdit(c)}>
                              <span className="text-sm">{c.CropName}</span>
                              {selectedCrop?.ID === c.ID && <Icon icon="lucide:check" style={{ fontSize: 14 }} />}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Variety */}
              <div className="col-md-6 col-lg-5">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2">Variety <span className="text-danger">*</span></label>
                <div className="position-relative" ref={varietyRef}>
                  <button type="button" className="form-select text-start d-flex align-items-center justify-content-between" onClick={() => setVarietyDropdownOpen(!varietyDropdownOpen)} disabled={!selectedCrop || loadingVarieties} style={{ minHeight: 40 }}>
                    <span className={`text-truncate ${selectedVariety ? "text-primary-light fw-medium" : "text-secondary-light"}`}>
                      {!selectedCrop ? "Select a crop first..." : loadingVarieties ? "Loading varieties..." : selectedVariety ? selectedVariety.VariantName : "Select Variety..."}
                    </span>
                  </button>
                  {varietyDropdownOpen && selectedCrop && (
                    <div className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden" style={{ zIndex: 30 }}>
                      <div className="p-8 border-bottom">
                        <div className="navbar-search">
                          <input className="bg-base h-36-px ps-30 w-100" placeholder="Search variety..." value={varietySearchTerm} onChange={(e) => setVarietySearchTerm(e.target.value)} autoFocus />
                          <Icon icon="ion:search-outline" className="icon" />
                        </div>
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: 240 }}>
                        {loadingVarieties ? (
                          <div className="p-16 text-center text-secondary-light text-sm">Loading varieties...</div>
                        ) : filteredVarieties.length === 0 ? (
                          <div className="p-16 text-center text-secondary-light text-sm">{varieties.length === 0 ? "No varieties found for this crop" : "No matching varieties"}</div>
                        ) : (
                          filteredVarieties.map((v) => (
                            <div key={v.ID} className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${selectedVariety?.ID === v.ID ? "bg-success-focus text-success-600 fw-medium" : "text-secondary-light"}`} onClick={() => handleSelectVarietyInEdit(v)}>
                              <span className="text-sm">{v.VariantName}</span>
                              {selectedVariety?.ID === v.ID && <Icon icon="lucide:check" style={{ fontSize: 14 }} />}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Phase count */}
              <div className="col-md-12 col-lg-2">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2 d-block">&nbsp;</label>
                <div className="px-12 py-8 bg-light radius-8 text-center" style={{ minHeight: 40 }}>
                  <div className="text-xs text-secondary-light">Phases</div>
                  <div className="fw-semibold text-primary-light text-sm">{selectedVariety ? matrixPhases.length : "—"}</div>
                </div>
              </div>
            </div>

            {/* Empty states */}
            {!selectedCrop ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon icon="mdi:barley" style={{ fontSize: 40 }} className="text-secondary-light mb-12" />
                <h6 className="fw-semibold text-primary-light mb-2">Select a Crop to begin</h6>
                <p className="text-sm text-secondary-light mb-0">Pick a crop from the dropdown above. You can add nutrients for any crop even if it has no data yet.</p>
              </div>
            ) : !selectedVariety ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon icon="mdi:seed" style={{ fontSize: 40 }} className="text-secondary-light mb-12" />
                <h6 className="fw-semibold text-primary-light mb-2">Now select a Variety for "{selectedCrop.CropName}"</h6>
                <p className="text-sm text-secondary-light mb-0">The nutrient matrix will appear with all configured phases as columns.</p>
              </div>
            ) : matrixLoading ? (
              <div className="text-center py-48">
                <span className="spinner-border text-primary spinner-border-sm me-2" role="status" />
                <span className="text-secondary-light">Loading phase-wise nutrient requirements...</span>
              </div>
            ) : matrixPhases.length === 0 ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon icon="lucide:alert-triangle" style={{ fontSize: 36 }} className="text-warning-600 mb-12" />
                <h6 className="fw-semibold text-primary-light mb-2">No Growth Phases Configured</h6>
                <p className="text-sm text-secondary-light mb-0">This variety does not have any active growth phases assigned in Master Phases.</p>
              </div>
            ) : (
              <>
                {/* Action bar */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-16 p-12 bg-light radius-8">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="px-12 py-4 radius-4 fw-medium text-xs bg-primary-focus text-primary-600 border border-primary-main">{selectedCrop.CropName} · {selectedVariety.VariantName}</span>
                    {hasExistingSavedData ? (
                      <span className="px-12 py-4 radius-4 fw-medium text-xs bg-success-focus text-success-600 border border-success-main d-inline-flex align-items-center gap-1">
                        <Icon icon="lucide:check" style={{ fontSize: 12 }} /> Existing values loaded
                      </span>
                    ) : (
                      <span className="px-12 py-4 radius-4 fw-medium text-xs bg-warning-focus text-warning-600 border border-warning-main">New entry</span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <button type="button" className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs" onClick={handleFillZeroEmpty}>
                      <Icon icon="lucide:plus" style={{ fontSize: 14 }} /> Fill Zero
                    </button>
                    <button type="button" className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs" onClick={handleResetToSaved}>
                      <Icon icon="lucide:rotate-ccw" style={{ fontSize: 14 }} /> Reset
                    </button>
                    <button type="button" className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs text-danger-600" onClick={handleClearAll}>
                      <Icon icon="lucide:x" style={{ fontSize: 14 }} /> Clear
                    </button>
                  </div>
                </div>

                {/* Matrix */}
                <div className="table-responsive radius-8 border mb-20" style={{ maxHeight: 600 }}>
                  <table className="table bordered-table sm-table mb-0 nutrient-matrix-table" style={{ minWidth: 560, width: "100%", tableLayout: "fixed" }}>
                    <thead className="table-light sticky-top" style={{ top: 0, zIndex: 2 }}>
                      <tr>
                        <th className="text-start" style={{ width: 170, minWidth: 170, position: "sticky", left: 0, zIndex: 3, background: "#f9fafb" }}>Nutrient</th>
                        {matrixPhases.map((phase) => {
                          const pId = String(phase.PhaseID || phase.id);
                          const hasDays = phase.FromDays !== null && phase.ToDays !== null && phase.FromDays !== undefined && phase.ToDays !== undefined;
                          return (
                            <th key={pId} className="text-center" style={{ minWidth: 90 }}>
                              <div className="fw-semibold text-primary-light text-xs text-truncate">{phase.PhaseName}</div>
                              <div className="text-xs text-secondary-light mt-1">{hasDays && `${phase.FromDays}-${phase.ToDays}d`}</div>
                            </th>
                          );
                        })}
                        <th className="text-center" style={{ width: 90, minWidth: 90, background: "#f9fafb" }}>
                          <span className="text-xs">Total</span>
                          <div className="text-xs text-secondary-light mt-1 fw-normal">kg/ha</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {NUTRIENT_COLUMNS.map((nutrient) => {
                        const rowTotal = calculateNutrientTotal(nutrient.key);
                        return (
                          <tr key={nutrient.key}>
                            <td style={{ position: "sticky", left: 0, zIndex: 1, background: "#fff" }}>
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <span className="fw-medium text-primary-light text-xs text-truncate d-block">{nutrient.label}</span>
                              </div>
                            </td>
                            {matrixPhases.map((phase) => {
                              const pId = String(phase.PhaseID || phase.id);
                              const cellVal = matrixValues[nutrient.key]?.[pId] ?? "";
                              return (
                                <td key={`${nutrient.key}_${pId}`} className="text-center p-2">
                                  <input type="text" inputMode="decimal" className="form-control form-control-sm text-center fw-medium" placeholder="0" value={cellVal} onChange={(e) => handleCellChange(nutrient.key, pId, e.target.value)} style={{ backgroundColor: cellVal !== "" ? "#f0fdf4" : "#fff", borderColor: cellVal !== "" ? "#86efac" : undefined, maxWidth: 78, margin: "0 auto", padding: "4px 6px", fontSize: 13 }} />
                                </td>
                              );
                            })}
                            <td className="text-center fw-semibold text-primary-light bg-light">
                              {rowTotal !== "-" && rowTotal > 0 ? rowTotal : <span className="text-secondary-light">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer save */}
                <div className="pt-12 border-top d-flex justify-content-between flex-wrap gap-2">
                  <button type="button" className="btn btn-light text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2" onClick={handleBackToList}>
                    <Icon icon="lucide:arrow-left" style={{ fontSize: 16 }} />
                    <span>Back to List</span>
                  </button>
                  <button type="button" className="btn btn-yellow text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2 ms-auto" onClick={handleSaveMatrix} disabled={savingMatrix}>
                    {savingMatrix ? (
                      <><span className="spinner-border spinner-border-sm" role="status" /><span>Saving...</span></>
                    ) : (
                      <><Icon icon="mdi:content-save" style={{ fontSize: 16 }} /><span>Save Nutritive Values</span></>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {editTab === "excel" && (
          <div className="row g-4">
            {/* EXPORT */}
            <div className="col-lg-6">
              <div className="p-20 bg-light radius-12 h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-16">
                    <Icon icon="mdi:file-download-outline" style={{ fontSize: 22 }} className="text-primary-600" />
                    <div>
                      <h6 className="mb-0 fw-semibold text-primary-light">Export Nutritive Values</h6>
                      <p className="text-xs text-secondary-light mb-0">Select crops & varieties to export as Excel.</p>
                    </div>
                  </div>
                  <div className="mb-16">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label text-xs fw-semibold text-primary-light mb-0">Crops ({exportSelectedCrops.length}/{allCrops.length})</label>
                      <button type="button" className="btn btn-link btn-sm p-0 text-xs text-primary-600 text-decoration-none" onClick={handleSelectAllExportCrops}>
                        {exportSelectedCrops.length === allCrops.length ? "Deselect all" : "Select all"}
                      </button>
                    </div>
                    <div className="navbar-search mb-2">
                      <input className="bg-base h-36-px w-100" placeholder="Filter crops..." value={exportCropSearch} onChange={(e) => setExportCropSearch(e.target.value)} />
                      <Icon icon="ion:search-outline" className="icon" />
                    </div>
                    <div className="bg-base border radius-8 p-8 overflow-auto" style={{ maxHeight: 150 }}>
                      {allCrops.filter((c) => !exportCropSearch.trim() || c.CropName?.toLowerCase().includes(exportCropSearch.toLowerCase())).map((c) => {
                        const isChecked = exportSelectedCrops.includes(c.ID);
                        return (
                          <div key={c.ID} className="d-flex align-items-center gap-2 py-4 px-8 radius-4 cursor-pointer hover-bg-light" onClick={() => handleToggleExportCrop(c.ID)}>
                            <input className="form-check-input mt-0 cursor-pointer" type="checkbox" checked={isChecked} onChange={() => { }} />
                            <span className="text-sm text-secondary-light">{c.CropName}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mb-16">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label text-xs fw-semibold text-primary-light mb-0">Varieties ({exportSelectedVarieties.length}/{allAvailableExportVarieties.length})</label>
                      {allAvailableExportVarieties.length > 0 && (
                        <button type="button" className="btn btn-link btn-sm p-0 text-xs text-primary-600 text-decoration-none" onClick={handleSelectAllExportVarieties}>
                          {exportSelectedVarieties.length === allAvailableExportVarieties.length ? "Deselect all" : "Select all"}
                        </button>
                      )}
                    </div>
                    {allAvailableExportVarieties.length > 0 && (
                      <div className="navbar-search mb-2">
                        <input className="bg-base h-36-px w-100" placeholder="Filter varieties..." value={exportVarietySearch} onChange={(e) => setExportVarietySearch(e.target.value)} />
                        <Icon icon="ion:search-outline" className="icon" />
                      </div>
                    )}
                    <div className="bg-base border radius-8 p-8 overflow-auto" style={{ maxHeight: 160 }}>
                      {loadingExportVarieties ? (
                        <div className="text-center py-12 text-secondary-light text-xs">Loading varieties...</div>
                      ) : exportSelectedCrops.length === 0 ? (
                        <div className="text-center py-12 text-secondary-light text-xs">Select a crop first</div>
                      ) : allAvailableExportVarieties.length === 0 ? (
                        <div className="text-center py-12 text-secondary-light text-xs">No varieties found</div>
                      ) : (
                        allAvailableExportVarieties.filter((v) => !exportVarietySearch.trim() || v.VariantName?.toLowerCase().includes(exportVarietySearch.toLowerCase())).map((v) => {
                          const isChecked = exportSelectedVarieties.includes(v.ID);
                          return (
                            <div key={v.ID} className="d-flex align-items-center gap-2 py-4 px-8 radius-4 cursor-pointer hover-bg-light" onClick={() => handleToggleExportVariety(v.ID)}>
                              <input className="form-check-input mt-0 cursor-pointer" type="checkbox" checked={isChecked} onChange={() => { }} />
                              <span className="text-sm text-secondary-light">{v.VariantName}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
                <div className="pt-12 border-top">
                  <button type="button" className="btn btn-yellow w-100 text-sm py-10 radius-8 d-flex align-items-center justify-content-center gap-2" onClick={handleExportExcel} disabled={isExportingExcel || exportSelectedVarieties.length === 0}>
                    {isExportingExcel ? (
                      <><span className="spinner-border spinner-border-sm" role="status" /><span>Generating Excel...</span></>
                    ) : (
                      <><Icon icon="mdi:file-excel" style={{ fontSize: 16 }} /><span>Export Excel ({exportSelectedVarieties.length} varieties)</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* IMPORT */}
            <div className="col-lg-6">
              <div className="p-20 bg-light radius-12 h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-16">
                    <Icon icon="mdi:cloud-upload-outline" style={{ fontSize: 22 }} className="text-success-600" />
                    <div>
                      <h6 className="mb-0 fw-semibold text-primary-light">Import Nutritive Values</h6>
                      <p className="text-xs text-secondary-light mb-0">Upload a filled spreadsheet to bulk-update nutrients.</p>
                    </div>
                  </div>
                  <div className="bg-base p-12 radius-8 mb-16">
                    <div className="text-xs fw-semibold text-secondary-light text-uppercase mb-2">Instructions</div>
                    <ol className="text-xs text-secondary-light mb-0 ps-16 d-flex flex-column gap-1">
                      <li>Export the template using the left panel.</li>
                      <li>Fill decimal nutrient values for each phase.</li>
                      <li>Save and upload below (<code>.xlsx</code> or <code>.xls</code>).</li>
                      <li>Existing records will be updated</li>
                    </ol>
                  </div>
                  <div className="p-24 border border-dashed radius-12 text-center bg-base cursor-pointer hover-border-primary mb-16" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" accept=".xlsx, .xls" className="d-none" onChange={handleFileChange} />
                    {importFile ? (
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon icon="mdi:file-excel" style={{ fontSize: 26 }} className="text-success-600" />
                        <div className="text-start">
                          <div className="fw-medium text-primary-light text-sm">{importFile.name}</div>
                          <div className="text-xs text-secondary-light">{(importFile.size / 1024).toFixed(1)} KB · Ready</div>
                        </div>
                        <button type="button" className="btn btn-link text-danger-600 p-0 ms-2" onClick={(e) => { e.stopPropagation(); handleRemoveImportFile(); }}>
                          <Icon icon="lucide:x" style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Icon icon="lucide:upload-cloud" style={{ fontSize: 28 }} className="text-primary-600 mb-2" />
                        <p className="text-sm fw-medium text-primary-light mb-1">Click to browse or drag & drop Excel</p>
                        <span className="text-xs text-secondary-light">Accepts .xlsx or .xls</span>
                      </>
                    )}
                  </div>
                  {importSuccessInfo && (
                    <div className="p-12 radius-8 bg-success-focus text-success-600 border border-success-main mb-16 d-flex align-items-start gap-2">
                      <Icon icon="lucide:check-circle" style={{ fontSize: 14 }} className="mt-1 flex-shrink-0" />
                      <div className="text-xs">
                        <div className="fw-semibold mb-1">Import Completed</div>
                        <div>{importSuccessInfo.varieties_count !== undefined ? `Updated ${importSuccessInfo.varieties_count} varieties with ${importSuccessInfo.records_count} phase records.` : importSuccessInfo.message}</div>
                      </div>
                    </div>
                  )}
                  {importErrors.length > 0 && (
                    <div className="p-12 radius-8 bg-danger-focus text-danger-600 border border-danger-main mb-16 overflow-auto" style={{ maxHeight: 180 }}>
                      <div className="d-flex align-items-center gap-2 fw-semibold text-xs mb-2">
                        <Icon icon="lucide:alert-circle" style={{ fontSize: 14 }} />
                        <span>Validation Errors ({importErrors.length})</span>
                      </div>
                      <ul className="text-xs mb-0 ps-16">
                        {importErrors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="pt-12 border-top">
                  <button type="button" className="btn btn-yellow w-100 text-sm py-10 radius-8 d-flex align-items-center justify-content-center gap-2" onClick={handleImportExcel} disabled={isImporting || !importFile}>
                    {isImporting ? (
                      <><span className="spinner-border spinner-border-sm" role="status" /><span>Validating & Importing...</span></>
                    ) : (
                      <><Icon icon="mdi:cloud-upload" style={{ fontSize: 16 }} /><span>Upload & Import</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return viewMode === "list" ? renderListView() : renderEditView();
};

export default VarietyWiseNutrientList;
